import { useMemo } from 'react';
import { useLanguageStore } from '@/context/LanguageStore';
import { useQuery } from '@tanstack/react-query';
import { useFetchWizardStructure, useProductConfig } from './useFetchData';
import { searchProducts } from '@/helpers/wizard';
import consts from '@/config/consts';
import axios from 'axios';

const searchApi = axios.create({
  baseURL: consts.searchUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

const queryContent = async ({
  manifestId,
  lang,
  term,
  forceGlobalSearch = false,
}: {
  manifestId: string;
  lang: string;
  term: string;
  forceGlobalSearch?: boolean;
}) => {
  const filter = forceGlobalSearch
    ? `languageCode eq '${lang}'`
    : `manifestId eq '${manifestId}' and languageCode eq '${lang}'`;

  const res = await searchApi.post<Elvita.SearchResponse>(
    `/${consts.implementation}/${consts.contentVersion}/content`,
    {
      search: `${term}*`,
      filter: filter,
    },
  );
  return res.data;
};

type SearchResult = {
  data: Elvita.SearchResultDocument[] | Elvita.ProductSearchDocument[] | undefined;
  isLoading: boolean;
  isError: boolean;
};

export default function useSearch(
  term: string,
  type: Elvita.SearchType,
  forceGlobalSearch = false,
): SearchResult {
  const lang = useLanguageStore(state => state.lang);
  const { data: productConfig } = useProductConfig();
  // Product search is resolved client-side from the wizard tree (already
  // loaded for navigation) instead of the productconfigs-derived search
  // index. Only content search still queries the search API.
  const wizardQuery = useFetchWizardStructure();

  const shouldUseProductContext = !forceGlobalSearch && type === 'content';

  const contentQuery = useQuery<Elvita.SearchResultDocument[]>({
    queryKey: [
      'search',
      term,
      lang,
      shouldUseProductContext ? productConfig?.ManifestId : null,
    ],
    queryFn: async () => {
      const data = await queryContent({
        lang,
        term,
        manifestId: productConfig?.ManifestId ?? '',
        forceGlobalSearch: !shouldUseProductContext,
      });
      return data.value;
    },
    enabled:
      type === 'content' &&
      term.length > 2 &&
      (forceGlobalSearch || !shouldUseProductContext || !!productConfig) &&
      !!lang,
  });

  const productResults = useMemo(
    () =>
      type === 'product' && term.length > 2 && wizardQuery.data?.data
        ? searchProducts(wizardQuery.data.data, term, lang)
        : undefined,
    [type, term, lang, wizardQuery.data],
  );

  if (type === 'product') {
    return {
      data: productResults,
      isLoading: wizardQuery.isLoading,
      isError: wizardQuery.isError,
    };
  }

  return contentQuery;
}
